import { DataRoomFile, DataRoomTree } from "@/types/dataRoom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import apiClient from "./apiClient"

const DATA_ROOM_KEY = ["dataRoom"]
const BASE_PATH = "/data-room"

interface UploadDataRoomFilesParams {
  files: File[]
  folderId: string | null
  /** When set, the single uploaded file is a revision replacing that file. */
  supersedesFileId?: string
  /** Fires with the fraction (0..1) of request bytes uploaded so far. Only
   * called when the browser reports a byte total — a chunked/unknown-length
   * request never fires it, leaving the caller's last known value in place. */
  onProgress?: (fraction: number) => void
}

interface UploadDataRoomFilesResult {
  uploaded: DataRoomFile[]
  failed: { fileName: string; code: string; message: string }[]
}

interface MoveFileParams {
  fileId: string
  folderId: string | null
}

interface UpdateFileNoteParams {
  fileId: string
  customerNote: string
}

// No permission gate: the `/data-room` router is customer-facing and
// org-scoped only (see backend/turnus_ai/api/data_room.py docstring) — during
// onboarding TEAM_MANAGEMENT is off and permission rows may not exist, so
// these mutations use plain useMutation, not useGuardedMutation.
const dataRoomApi = {
  getTree: async (): Promise<DataRoomTree> => {
    const response = await apiClient.get<DataRoomTree>(BASE_PATH)
    return response.data
  },

  createFolder: async (params: {
    name: string
    parentId?: string | null
  }): Promise<void> => {
    await apiClient.post(`${BASE_PATH}/folders`, params)
  },

  updateFolder: async (params: {
    folderId: string
    name: string
  }): Promise<void> => {
    await apiClient.patch(`${BASE_PATH}/folders/${params.folderId}`, {
      name: params.name,
    })
  },

  deleteFolder: async (folderId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/folders/${folderId}`)
  },

  uploadFiles: async (
    params: UploadDataRoomFilesParams,
  ): Promise<UploadDataRoomFilesResult> => {
    const formData = new FormData()
    params.files.forEach((file) => formData.append("files", file))
    if (params.folderId) {
      formData.append("folder_id", params.folderId)
    }
    if (params.supersedesFileId) {
      formData.append("supersedes_file_id", params.supersedesFileId)
    }
    const response = await apiClient.post<UploadDataRoomFilesResult>(
      `${BASE_PATH}/files`,
      formData,
      {
        onUploadProgress: (event) => {
          if (!event.total) return
          params.onProgress?.(event.loaded / event.total)
        },
      },
    )
    return response.data
  },

  // Shared PATCH `/data-room/files/{id}` used by every partial file update
  // (move, note edit, …) — apiClient's request interceptor snake_cases the
  // body, so `{ folderId }` / `{ customerNote }` reach the wire as
  // `{ folder_id }` / `{ customer_note }`.
  updateFile: async (
    params: { fileId: string } & (
      | { folderId: string | null }
      | { customerNote: string }
    ),
  ): Promise<DataRoomFile> => {
    const { fileId, ...body } = params
    const response = await apiClient.patch<DataRoomFile>(
      `${BASE_PATH}/files/${fileId}`,
      body,
    )
    return response.data
  },

  moveFile: async (params: MoveFileParams): Promise<void> => {
    await dataRoomApi.updateFile({
      fileId: params.fileId,
      folderId: params.folderId,
    })
  },

  deleteFile: async (fileId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/files/${fileId}`)
  },

  getDownloadUrl: async (fileId: string): Promise<string> => {
    const response = await apiClient.get<{ url: string }>(
      `${BASE_PATH}/files/${fileId}/download`,
    )
    return response.data.url
  },

  // Raw bytes for in-modal preview (spreadsheet/image), as opposed to
  // `getDownloadUrl`'s presigned URL used by the PDF iframe and the download
  // button. `responseType: "arraybuffer"` makes apiClient's response
  // interceptor skip case conversion (guarded for ArrayBuffer/Blob). `signal`
  // is the caller's (react-query's) AbortSignal, forwarded so the underlying
  // request actually aborts on unmount/refetch instead of only having its
  // resolved result discarded.
  getFileContent: async (
    fileId: string,
    signal?: AbortSignal,
  ): Promise<ArrayBuffer> => {
    const response = await apiClient.get<ArrayBuffer>(
      `${BASE_PATH}/files/${fileId}/content`,
      { responseType: "arraybuffer", signal },
    )
    return response.data
  },
}

export const useDataRoomTree = () => {
  return useQuery({
    queryKey: DATA_ROOM_KEY,
    queryFn: dataRoomApi.getTree,
  })
}

export const useCreateFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { name: string; parentId?: string | null }) =>
      dataRoomApi.createFolder(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_ROOM_KEY })
    },
  })
}

export const useUpdateFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { folderId: string; name: string }) =>
      dataRoomApi.updateFolder(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_ROOM_KEY })
    },
  })
}

export const useDeleteFolder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (folderId: string) => dataRoomApi.deleteFolder(folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_ROOM_KEY })
    },
  })
}

export const useUploadDataRoomFiles = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: UploadDataRoomFilesParams) =>
      dataRoomApi.uploadFiles(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_ROOM_KEY })
    },
  })
}

export const useMoveFile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: MoveFileParams) => dataRoomApi.moveFile(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_ROOM_KEY })
    },
  })
}

export const useDeleteDataRoomFile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fileId: string) => dataRoomApi.deleteFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_ROOM_KEY })
    },
  })
}

// Plain export (mirrors `getDataRoomDownloadUrl`/`updateDataRoomFile` below)
// so the `onUploadProgress` wiring in `dataRoomApi.uploadFiles` can be
// exercised directly, without going through `useMutation`/`renderHook`.
export const uploadDataRoomFiles = (
  params: UploadDataRoomFilesParams,
): Promise<UploadDataRoomFilesResult> => dataRoomApi.uploadFiles(params)

export const getDataRoomDownloadUrl = (fileId: string): Promise<string> =>
  dataRoomApi.getDownloadUrl(fileId)

export const getDataRoomFileContent = (
  fileId: string,
  signal?: AbortSignal,
): Promise<ArrayBuffer> => dataRoomApi.getFileContent(fileId, signal)

export const updateDataRoomFile = (
  fileId: string,
  params: { customerNote: string },
): Promise<DataRoomFile> =>
  dataRoomApi.updateFile({ fileId, customerNote: params.customerNote })

export const useUpdateDataRoomFileNote = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: UpdateFileNoteParams) =>
      updateDataRoomFile(params.fileId, { customerNote: params.customerNote }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_ROOM_KEY })
    },
  })
}
